<script setup>
	const packages = [
		{
			name: "Innovator's Package",
			price: "$2,500",
			cadence: "one-time",
			description: "A working MVP in two to three weeks.",
			points: ["Web or mobile app", "Design and backend included", "Built to test a real idea"],
			bestFor: "Prototypes and proofs of concept",
		},
		{
			name: "Starter Package",
			price: "$800",
			cadence: "/mo",
			description: "Lightweight, ongoing development support.",
			points: ["~10 hours every month", "Fixes, updates, optimizations", "QA and deployment included"],
			bestFor: "Stable products that need upkeep",
		},
		{
			name: "Maintenance Package",
			price: "$2,250",
			cadence: "/mo",
			description: "Dedicated development for growing teams.",
			points: ["~30 hours every month", "Features, fixes and maintenance", "Slack plus weekly standups"],
			bestFor: "Teams without in-house developers",
		},
	];

	const customPackages = [
		{
			name: "CTO-as-a-Service",
			price: "Custom",
			cadence: "",
			description: "Senior technical leadership without a full-time hire.",
			points: ["Architecture and roadmap", "Vendor and technology decisions", "Team and quality oversight"],
			bestFor: "Non-technical founders and scaling teams",
		},
		{
			name: "Project-Based Work",
			price: "Custom",
			cadence: "",
			description: "Fixed-scope builds, start to finish.",
			points: ["MVPs, redesigns, migrations", "Fixed or milestone pricing", "Clear timelines, dedicated people"],
			bestFor: "One-time builds and product launches",
		},
	];

	const techStack = ["React", "Node.js", "C# / .NET", "Python", "PostgreSQL", "MongoDB", "Azure", "+ more"];
</script>

<template>
	<div class="services">
		<section class="hero">
			<div class="hero-inner">
				<span class="offer-pill">50% off your first 6 weeks</span>
				<h1 class="hero-title">Senior-led software, without the agency overhead</h1>
				<p class="hero-lead">
					Fixing an AI-built mess, finishing a half-done product, or starting fresh — you
					work directly with engineers who have shipped real systems for two decades.
				</p>
				<div class="hero-actions">
					<RouterLink :to="{ name: 'contact' }" class="btn btn-primary">Book a free consult</RouterLink>
					<RouterLink :to="{ name: 'how-we-work' }" class="btn btn-ghost">How we work</RouterLink>
				</div>
			</div>
		</section>

		<section class="packages">
			<div class="packages-inner">
				<header class="block-header">
					<span class="block-eyebrow">Pricing</span>
					<h2 class="block-heading">Simple packages, real outcomes</h2>
					<p class="block-sub">Pick a starting point. We'll tell you honestly if a different one fits better — or build a custom plan around what you actually need.</p>
				</header>

				<div class="pkg-grid">
					<article v-for="pkg in packages" :key="pkg.name" class="pkg">
						<span class="pkg-name">{{ pkg.name }}</span>
						<div class="pkg-price">{{ pkg.price }}<span v-if="pkg.cadence">{{ pkg.cadence }}</span></div>
						<p class="pkg-desc">{{ pkg.description }}</p>
						<ul class="pkg-list">
							<li v-for="point in pkg.points" :key="point">{{ point }}</li>
						</ul>
						<p class="pkg-best"><strong>Best for:</strong> {{ pkg.bestFor }}</p>
					</article>
				</div>

				<div class="pkg-grid pkg-grid-wide">
					<article v-for="pkg in customPackages" :key="pkg.name" class="pkg">
						<span class="pkg-name">{{ pkg.name }}</span>
						<div class="pkg-price">{{ pkg.price }}<span v-if="pkg.cadence">{{ pkg.cadence }}</span></div>
						<p class="pkg-desc">{{ pkg.description }}</p>
						<ul class="pkg-list">
							<li v-for="point in pkg.points" :key="point">{{ point }}</li>
						</ul>
						<p class="pkg-best"><strong>Best for:</strong> {{ pkg.bestFor }}</p>
					</article>
				</div>
			</div>
		</section>

		<section class="tech">
			<div class="tech-inner">
				<span class="block-eyebrow">Tech we work with</span>
				<h2 class="tech-heading">A modern, proven stack</h2>
				<p class="tech-sub">We pick the right tool for the job, so you get software that's reliable and easy to maintain.</p>
				<ul class="tech-pills">
					<li v-for="tech in techStack" :key="tech" class="tech-pill">{{ tech }}</li>
				</ul>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="cta-card">
					<h2 class="cta-title">Not sure which one fits?</h2>
					<p class="cta-lead">Tell us what's going on and we'll point you to the right starting place — no pressure, no jargon.</p>
					<div class="cta-actions">
						<RouterLink :to="{ name: 'contact' }" class="btn btn-on-blue">Book a free consult</RouterLink>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style scoped>
	.services {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Poppins", "Plus Jakarta Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-ghost {
		background: var(--surface);
		color: var(--ink);
		border-color: var(--border-strong);
	}

	.btn-ghost:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-on-blue {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.5);
	}

	.btn-on-blue:hover {
		transform: translateY(-2px);
		background: #f1f6ff;
		color: var(--blue-deep);
		box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.55);
	}

	.hero {
		padding: clamp(3.5rem, 8vw, 6rem) 1.5rem clamp(2.5rem, 5vw, 4rem);
	}

	.hero-inner {
		max-width: 920px;
		margin: 0 auto;
		text-align: center;
	}

	.offer-pill {
		display: inline-block;
		padding: 0.5rem 1.15rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.92rem;
		font-weight: 700;
		margin-bottom: 1.4rem;
	}

	.hero-title {
		font-size: clamp(2.2rem, 4.2vw, 3.4rem);
		line-height: 1.08;
		font-weight: 800;
		letter-spacing: -0.025em;
		color: var(--navy);
		margin: 0 0 1.25rem;
	}

	.hero-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 60ch;
		margin: 0 auto 2rem;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	.block-header {
		text-align: center;
		margin-bottom: 2.75rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.block-heading {
		font-size: clamp(1.85rem, 3vw, 2.7rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0;
	}

	.block-sub {
		max-width: 56ch;
		margin: 0.9rem auto 0;
		font-size: clamp(1.02rem, 1.2vw, 1.18rem);
		line-height: 1.65;
		color: var(--ink-soft);
	}

	.packages {
		padding: clamp(3rem, 7vw, 5.5rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.packages-inner {
		max-width: 1140px;
		margin: 0 auto;
	}

	.pkg-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.5rem;
	}

	.pkg-grid-wide {
		grid-template-columns: repeat(2, 1fr);
		margin-top: 1.5rem;
	}

	.pkg {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 1.9rem 1.6rem;
		border: 1px solid var(--border);
		border-radius: 18px;
		background: var(--bg);
		transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
	}

	.pkg:hover {
		transform: translateY(-4px);
		border-color: var(--blue);
		box-shadow: 0 24px 44px -30px rgba(15, 39, 66, 0.45);
	}

	.pkg-name {
		font-size: 0.8rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--blue-deep);
	}

	.pkg-price {
		font-size: 1.8rem;
		font-weight: 800;
		color: var(--navy);
		letter-spacing: -0.02em;
		margin: 0.35rem 0 0.1rem;
	}

	.pkg-price span {
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink-muted);
		margin-left: 0.2rem;
	}

	.pkg-desc {
		color: var(--ink-soft);
		font-size: 1rem;
		line-height: 1.55;
		margin: 0.4rem 0 1.2rem;
	}

	.pkg-list {
		list-style: none;
		margin: 0 0 1.4rem;
		padding: 0;
		display: grid;
		gap: 0.65rem;
	}

	.pkg-list li {
		position: relative;
		padding-left: 1.7rem;
		font-size: 0.97rem;
		line-height: 1.45;
		color: var(--ink);
	}

	.pkg-list li::before {
		content: "✓";
		position: absolute;
		left: 0;
		top: 0.05rem;
		width: 1.2rem;
		height: 1.2rem;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.72rem;
		font-weight: 800;
	}

	.pkg-best {
		margin: auto 0 0;
		font-size: 0.9rem;
		color: var(--ink-muted);
		line-height: 1.5;
	}

	.pkg-best strong {
		color: var(--ink);
		font-weight: 700;
	}

	.tech {
		padding: clamp(2.75rem, 5vw, 4rem) 1.5rem;
		background: var(--bg);
	}

	.tech-inner {
		max-width: 1000px;
		margin: 0 auto;
		padding: clamp(1.85rem, 3vw, 2.5rem);
		border: 1px solid var(--border);
		border-radius: 20px;
		background: var(--surface);
		text-align: center;
		box-shadow: 0 30px 60px -44px rgba(15, 39, 66, 0.4);
	}

	.tech-heading {
		font-size: clamp(1.5rem, 2.4vw, 2.1rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0.55rem 0 0;
	}

	.tech-sub {
		max-width: 46ch;
		margin: 0.6rem auto 0;
		color: var(--ink-soft);
		line-height: 1.6;
	}

	.tech-pills {
		list-style: none;
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.6rem;
		margin: 1.3rem 0 0;
		padding: 0;
	}

	.tech-pill {
		padding: 0.5rem 0.95rem;
		border-radius: 999px;
		background: var(--surface-muted);
		border: 1px solid var(--border);
		color: var(--ink);
		font-weight: 600;
		font-size: 0.92rem;
	}

	.cta {
		padding: clamp(3rem, 6vw, 5rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.cta-inner {
		max-width: 1000px;
		margin: 0 auto;
	}

	.cta-card {
		padding: clamp(3rem, 5vw, 4.25rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 24px;
		background: linear-gradient(135deg, var(--blue) 0%, var(--sky-deep) 100%);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(37, 99, 235, 0.7);
	}

	.cta-title {
		font-size: clamp(1.9rem, 3.5vw, 2.8rem);
		font-weight: 800;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.25rem;
		max-width: 20ch;
		color: #ffffff;
	}

	.cta-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.25rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 52ch;
		margin: 0 auto 2rem;
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	@media (max-width: 900px) {
		.pkg-grid,
		.pkg-grid-wide { grid-template-columns: 1fr; }
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }
	}
</style>